import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import confetti from "canvas-confetti";
import { QRCodeSVG } from "qrcode.react";
import {
  Send, QrCode, Coins, Copy, Check, Camera, Search, ScanLine, Sparkles, X, Timer,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fireCoinConfetti() {
  const opts = {
    spread: 90, startVelocity: 40, ticks: 180, scalar: 1,
    colors: ["#fbc02d", "#ff9800", "#f6d365", "#fde68a"],
    origin: { y: 0.35 },
  };
  confetti({ ...opts, particleCount: 100, angle: 90, origin: { x: 0.5, y: 0.3 } });
}

/**
 * CoinTransferDialog — modal that lets the user send coins in 2 ways:
 *  1. Direct: email or phone lookup + amount
 *  2. QR: emit a signed QR (SVG) for the recipient to scan / paste
 *  Also lets the current user REDEEM a QR they received (paste or camera).
 */
export default function CoinTransferDialog({ trigger, onDone }) {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button data-testid="coin-transfer-open" className="rounded-full gap-1">
            <Send className="w-4 h-4" /> Transferir moedas
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="coin-transfer-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Coins className="w-5 h-5 text-primary" /> Enviar moedas FUTURO PDV
          </DialogTitle>
        </DialogHeader>
        <Tabs defaultValue="direct">
          <TabsList className="rounded-full grid grid-cols-3">
            <TabsTrigger value="direct" className="rounded-full gap-1" data-testid="tab-transfer-direct">
              <Send className="w-3.5 h-3.5" /> Direto
            </TabsTrigger>
            <TabsTrigger value="qr" className="rounded-full gap-1" data-testid="tab-transfer-qr">
              <QrCode className="w-3.5 h-3.5" /> Gerar QR
            </TabsTrigger>
            <TabsTrigger value="scan" className="rounded-full gap-1" data-testid="tab-transfer-scan">
              <ScanLine className="w-3.5 h-3.5" /> Receber
            </TabsTrigger>
          </TabsList>
          <TabsContent value="direct" className="mt-4">
            <DirectPanel onDone={() => { setOpen(false); onDone?.(); }} />
          </TabsContent>
          <TabsContent value="qr" className="mt-4">
            <QRIssuePanel />
          </TabsContent>
          <TabsContent value="scan" className="mt-4">
            <QRRedeemPanel onDone={() => { setOpen(false); onDone?.(); }} />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Direct transfer ----------
function DirectPanel({ onDone }) {
  const [to, setTo] = useState("");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const lookup = async () => {
    if (!to.trim()) return;
    setBusy(true);
    setPreview(null);
    try {
      const r = await axios.post(`${API}/coins/lookup`, { to: to.trim() });
      setPreview(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Não encontrado");
    }
    setBusy(false);
  };

  const send = async () => {
    const amt = parseInt(amount, 10);
    if (!preview) return toast.error("Confirme o destinatário");
    if (!amt || amt <= 0) return toast.error("Informe um valor válido");
    setBusy(true);
    try {
      const r = await axios.post(`${API}/coins/transfer`, {
        to: preview.user_id,
        amount: amt,
        note,
        idempotency_key: `direct-${preview.user_id}-${Date.now()}`,
      });
      fireCoinConfetti();
      toast.success(`Enviou ${r.data.amount} moedas para ${r.data.to_user_name || "usuário"}!`);
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro na transferência");
    }
    setBusy(false);
  };

  return (
    <div className="space-y-3" data-testid="coin-direct-panel">
      <div className="flex gap-2">
        <Input
          data-testid="coin-direct-to"
          placeholder="E-mail, telefone ou user_id do destinatário"
          value={to}
          onChange={(e) => { setTo(e.target.value); setPreview(null); }}
          onKeyDown={(e) => e.key === "Enter" && lookup()}
        />
        <Button
          variant="outline"
          className="rounded-full gap-1"
          onClick={lookup}
          disabled={busy || !to.trim()}
          data-testid="coin-direct-lookup"
        >
          <Search className="w-4 h-4" /> Buscar
        </Button>
      </div>
      {preview && (
        <Card className="p-3 flex items-center gap-3 border-primary/40 bg-primary/5" data-testid="coin-direct-preview">
          <Avatar className="w-10 h-10">
            <AvatarImage src={preview.picture} />
            <AvatarFallback>{preview.name?.[0]}</AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold truncate">{preview.name}</div>
            <div className="text-[11px] text-muted-foreground font-mono truncate">{preview.user_id}</div>
          </div>
          <Check className="w-4 h-4 text-emerald-500" />
        </Card>
      )}
      <Input
        data-testid="coin-direct-amount"
        type="number"
        placeholder="Quantas moedas?"
        min="1"
        value={amount}
        onChange={(e) => setAmount(e.target.value)}
      />
      <Textarea
        data-testid="coin-direct-note"
        rows={2}
        placeholder="Mensagem (opcional)"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        maxLength={200}
      />
      <Button
        onClick={send}
        disabled={busy || !preview || !amount}
        className="w-full rounded-full gap-1"
        data-testid="coin-direct-send"
      >
        <Send className="w-4 h-4" /> Enviar agora
      </Button>
      <p className="text-[10px] text-muted-foreground text-center">
        Transferência instantânea. Verifique o destinatário antes de confirmar — moedas enviadas não podem ser desfeitas.
      </p>
    </div>
  );
}

// ---------- QR issue ----------
function QRIssuePanel() {
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [qr, setQr] = useState(null);
  const [remaining, setRemaining] = useState(0);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!qr?.expires_at) return;
    const iv = setInterval(() => {
      const diff = Math.max(0, Math.floor((new Date(qr.expires_at).getTime() - Date.now()) / 1000));
      setRemaining(diff);
      if (diff === 0) clearInterval(iv);
    }, 500);
    return () => clearInterval(iv);
  }, [qr]);

  const generate = async () => {
    const amt = parseInt(amount, 10);
    if (!amt || amt <= 0) return toast.error("Informe um valor válido");
    try {
      const r = await axios.post(`${API}/coins/qr`, { amount: amt, note, ttl_seconds: 600 });
      setQr(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro");
    }
  };

  const copy = () => {
    if (!qr?.qr_code) return;
    navigator.clipboard.writeText(qr.qr_code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
    toast.success("Token copiado. Cole no scanner do destinatário.");
  };

  const cancel = () => { setQr(null); setAmount(""); setNote(""); };

  if (!qr) {
    return (
      <div className="space-y-3" data-testid="coin-qr-issue-form">
        <p className="text-xs text-muted-foreground">Gere um QR para o destinatário escanear ou colar. Ele saca automaticamente da sua carteira. Válido por 10 minutos.</p>
        <Input
          data-testid="coin-qr-amount"
          type="number"
          min="1"
          placeholder="Quantas moedas serão sacadas?"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
        />
        <Textarea
          data-testid="coin-qr-note"
          rows={2}
          placeholder="Mensagem (opcional)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          maxLength={200}
        />
        <Button onClick={generate} disabled={!amount} className="w-full rounded-full gap-1" data-testid="coin-qr-generate">
          <QrCode className="w-4 h-4" /> Gerar QR code
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4 text-center" data-testid="coin-qr-issue-preview">
      <div className="text-xs uppercase tracking-widest text-primary font-bold">Aponte a câmera do destinatário</div>
      <div className="text-3xl font-bold flex items-center justify-center gap-1">
        <Coins className="w-6 h-6 text-primary" /> {qr.amount}
      </div>
      <div className="inline-block p-3 bg-white rounded-2xl border border-border" data-testid="coin-qr-image">
        <QRCodeSVG value={qr.qr_code} size={220} includeMargin level="M" />
      </div>
      <div className="text-[11px] text-muted-foreground flex items-center justify-center gap-1" data-testid="coin-qr-remaining">
        <Timer className="w-3.5 h-3.5" />
        Expira em {Math.floor(remaining / 60)}:{String(remaining % 60).padStart(2, "0")}
      </div>
      <div className="flex gap-2 justify-center">
        <Button variant="outline" onClick={copy} className="rounded-full gap-1" data-testid="coin-qr-copy">
          {copied ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
          {copied ? "Copiado" : "Copiar token"}
        </Button>
        <Button variant="ghost" onClick={cancel} className="rounded-full gap-1" data-testid="coin-qr-cancel">
          <X className="w-4 h-4" /> Cancelar
        </Button>
      </div>
    </div>
  );
}

// ---------- QR redeem (scan / paste) ----------
function QRRedeemPanel({ onDone }) {
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanErr, setScanErr] = useState("");
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);

  const stopScan = () => {
    setScanning(false);
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  useEffect(() => () => stopScan(), []);

  const startScan = async () => {
    setScanErr("");
    if (!("BarcodeDetector" in window)) {
      setScanErr("Seu navegador não suporta câmera. Cole o token abaixo.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
      streamRef.current = stream;
      const video = videoRef.current;
      if (video) {
        video.srcObject = stream;
        await video.play();
      }
      setScanning(true);
      const detector = new window.BarcodeDetector({ formats: ["qr_code"] });
      const tick = async () => {
        if (!videoRef.current || !streamRef.current) return;
        try {
          const codes = await detector.detect(videoRef.current);
          if (codes.length) {
            const val = codes[0].rawValue;
            stopScan();
            setToken(val);
            await redeem(val);
            return;
          }
        } catch { /* ignore per-frame errors */ }
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    } catch (e) {
      setScanErr(e?.message || "Não foi possível acessar a câmera.");
    }
  };

  const redeem = async (val) => {
    const code = (val ?? token).trim();
    if (!code) return toast.error("Cole o token");
    setBusy(true);
    try {
      const r = await axios.post(`${API}/coins/qr/redeem`, { qr_code: code });
      fireCoinConfetti();
      toast.success(`Recebeu ${r.data.amount} moedas de ${r.data.from_user_name || "usuário"}!`);
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "QR inválido");
    }
    setBusy(false);
  };

  return (
    <div className="space-y-3" data-testid="coin-qr-redeem-panel">
      <p className="text-xs text-muted-foreground">Escaneie o QR code do remetente ou cole o token abaixo. A transferência é instantânea.</p>

      {!scanning && (
        <Button onClick={startScan} variant="outline" className="w-full rounded-full gap-1" data-testid="coin-qr-scan-start">
          <Camera className="w-4 h-4" /> Abrir câmera
        </Button>
      )}
      {scanning && (
        <div className="relative w-full aspect-square rounded-2xl overflow-hidden bg-black" data-testid="coin-qr-scan-video">
          <video ref={videoRef} className="w-full h-full object-cover" playsInline muted />
          <div className="absolute inset-8 border-4 border-primary/70 rounded-2xl pointer-events-none" />
          <button
            onClick={stopScan}
            className="absolute top-2 right-2 w-9 h-9 rounded-full bg-black/60 text-white flex items-center justify-center backdrop-blur"
            data-testid="coin-qr-scan-stop"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      {scanErr && <p className="text-[11px] text-destructive" data-testid="coin-qr-scan-error">{scanErr}</p>}

      <div className="pt-2 border-t border-border">
        <Textarea
          data-testid="coin-qr-token-input"
          rows={3}
          placeholder="Ou cole o token do QR aqui..."
          value={token}
          onChange={(e) => setToken(e.target.value)}
        />
        <Button
          onClick={() => redeem()}
          disabled={busy || !token.trim()}
          className="w-full rounded-full gap-1 mt-2"
          data-testid="coin-qr-redeem-btn"
        >
          <Sparkles className="w-4 h-4" /> Receber moedas
        </Button>
      </div>
    </div>
  );
}
